import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

export interface ClassData {
  id?: number;
  name: string;
  section: string;
  schoolId: number;
  sessionId?: number;
}

const API_URL = `${process.env.REACT_APP_API_URL}/classes`;

// Fetch classes by schoolId
const getClassesBySchool = async (schoolId: number): Promise<ClassData[]> => {
  const response = await axios.get(`${API_URL}/school/${schoolId}`);
  return response.data;
};

// Get class by ID
const getClassById = async (id: number): Promise<ClassData> => {
  const response = await axios.get(`${API_URL}/${id}`);
  return response.data;
};

// Create a new class
const createClass = async (classData: ClassData): Promise<ClassData> => {
  try {
    const response = await axios.post(API_URL, {
      ...classData,
      section: classData.section,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Update an existing class
const updateClass = async (id: number, classData: ClassData): Promise<ClassData> => {
  try {
    const response = await axios.put(`${API_URL}/${id}`, {
      ...classData,
      id,
      section: classData.section,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Delete a class
const deleteClass = async (id: number): Promise<void> => {
  await axios.delete(`${API_URL}/${id}`);
};

export function useClasses(schoolId?: number, tenantLoading: boolean = false) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const classesQuery = useQuery<ClassData[], Error>({
    queryKey: ["classes", schoolId],
    queryFn: () => {
      if (!schoolId) return Promise.resolve([]);
      return getClassesBySchool(schoolId);
    },
    enabled: !!schoolId && !tenantLoading,
    staleTime: 30000,
  });

  const createClassMutation = useMutation({
    mutationFn: createClass,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["classes"] });
    }
  });

  const updateClassMutation = useMutation({
    mutationFn: (args: { id: number; data: ClassData }) => 
      updateClass(args.id, args.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["classes"] });
    }
  });

  const deleteClassMutation = useMutation({
    mutationFn: deleteClass,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["classes"] }),
  });

  return {
    classesQuery,
    getClassById,
    createClassMutation,
    updateClassMutation,
    deleteClassMutation,
  };
}
